import { z } from 'zod';
import { RecurringStartAnchorSchema } from './opportunities';
// =====================================================================
// F42 — Plan de facturación de un contrato (oportunidad GANADA).
// El wizard crea en lote: hitos fijos + cuotas recurrentes (acotadas o no)
// + revenue share de partner. La previsión proyecta la facturación futura.
// =====================================================================
export const BillingPlanMilestoneInputSchema = z.object({
    title: z.string().min(1).max(300),
    description: z.string().max(2000).nullable().optional(),
    /** Base imponible en céntimos. */
    amountCents: z.number().int().positive(),
    /** Fracción (0.21 = 21%). */
    vatRate: z.number().min(0).max(1).default(0.21),
    /** ISO date (YYYY-MM-DD) de facturación prevista. */
    dueDate: z.string().min(10),
});
export const BillingPlanRecurringInputSchema = z.object({
    /** Nombre interno de la cuota ("Cuota mensual servicio IA Busquets"). */
    name: z.string().min(1).max(300),
    /** Descripción de la línea de la factura ("Servicio de consultoría IA — cuota mensual"). */
    concept: z.string().min(1).max(500),
    /** Base imponible por cuota en céntimos. */
    amountCents: z.number().int().positive(),
    vatRate: z.number().min(0).max(1).default(0.21),
    irpfRate: z.number().min(0).max(1).default(0),
    periodicity: z.enum(['MONTHLY', 'BIMONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'YEARLY']),
    /** Día del mes de emisión (1-28). */
    dayOfMonth: z.number().int().min(1).max(28).default(1),
    /** ISO date desde la que arranca la primera cuota. Default: hoy. */
    startDate: z.string().min(10).nullable().optional(),
    /**
     * Ancla de arranque: 'DATE' (usa startDate) o una fase de ejecución. Con una
     * fase aún no alcanzada, la iguala se crea PAUSED y se activa al llegar a ella.
     */
    startAnchor: RecurringStartAnchorSchema.nullable().optional(),
    /** Nº total de cuotas (18 = "18 mensualidades"). null = indefinida. */
    maxOccurrences: z.number().int().positive().nullable().optional(),
    /** Fecha fin alternativa (no generar más allá). */
    endsAt: z.string().min(10).nullable().optional(),
});
export const CreateBillingPlanSchema = z.object({
    /** Partner con revenue share sobre la facturación del contrato (ej. Libertia). */
    revenueSharePartner: z.string().max(200).nullable().optional(),
    /** Fracción (0.20 = 20%). Sin tope 8%: esto NO es la comisión CAC del advisor. */
    revenueSharePct: z.number().min(0).max(1).nullable().optional(),
    milestones: z.array(BillingPlanMilestoneInputSchema).default([]),
    recurrings: z.array(BillingPlanRecurringInputSchema).default([]),
});
export const CreateBillingPlanResponseSchema = z.object({
    milestoneIds: z.array(z.string()),
    recurringIds: z.array(z.string()),
    revenueShareApplied: z.boolean(),
});
// ---------- Previsión ----------
export const BillingForecastItemSchema = z.object({
    source: z.enum(['MILESTONE', 'RECURRING']),
    label: z.string(),
    clientName: z.string(),
    opportunityId: z.string().nullable(),
    opportunityTitle: z.string().nullable(),
    baseCents: z.number().int(),
    commissionCents: z.number().int(),
});
export const BillingForecastMonthSchema = z.object({
    /** YYYY-MM */
    month: z.string(),
    /** Base imponible proyectada (hitos pendientes + cuotas futuras). */
    projectedCents: z.number().int(),
    /** Comisiones revenue share proyectadas sobre esa base. */
    commissionCents: z.number().int(),
    /** projected − commission. */
    netCents: z.number().int(),
    /** Base imponible ya emitida en el mes (facturas no borrador/anuladas). */
    emittedCents: z.number().int(),
    /** Cobros del mes (importe total de pagos, IVA incluido). */
    collectedCents: z.number().int(),
    items: z.array(BillingForecastItemSchema),
});
export const BillingForecastResponseSchema = z.object({
    months: z.array(BillingForecastMonthSchema),
    /** Suma de projectedCents del rango. */
    totalProjectedCents: z.number().int(),
    totalCommissionCents: z.number().int(),
    totalNetCents: z.number().int(),
});
//# sourceMappingURL=billing-plan.js.map